import { FC, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";

import config from "config/index";
import CustomLink from "@Components/CustomLink";
import LazyImage from "@Components/Image";
import HelmetServer from "@Components/HelmetServer";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import TrustPilotCustomWidget from "@Components/TrustPilotCustomWidget";
import ChinaPaymentLogos from "@Components/ChinaPaymentLogos";
import AndroidStoreIcon from "@Icons/AndroidStoreIcon";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import getImagePath from "@Utils/getImagePath";
import useTranslationHook from "hooks/useTranslationHook";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import {
  APP_DOWNLOAD_APP_STORE_LINK,
  CHINA_ANDROID_DOWNLOAD_LINK,
  ICP_LICENCE_NUMBER,
  ICP_URL,
} from "constants/configConstants";

import {
  addCountryWiseContactInfo,
  CHINA_FOOTER_QR_TILES,
  footerListLeftChina,
  footerListRightChina,
  footerSupportListChinaDesktop,
} from "../../constant";
import getSiteNavSchema from "../../util";
import chinaSocialMediaData from "../FollowUs/chinaSocialMediaData";
import classes from "./FooterDesktopChina.module.scss";

const QR_SIZE = 100;

type FooterLink = {
  name: string;
  link: string;
  gaKey: string;
  extraText?: string;
};

type ContactDetail = {
  icon: JSX.Element;
  label: string;
  link: string;
  isVisibleForChina: boolean;
};
const FooterDesktopChina: FC = () => {
  const { t } = useTranslationHook();
  const customWindowOpen = useCustomWindowOpen();
  const year = new Date().getFullYear();

  const { country, nonce } = useSelector(
    ({ appData }: AppState) => ({ country: appData.country, nonce: appData.nonce }),
    shallowEqual,
  );

  const contactDetails = useMemo(
    () =>
      (addCountryWiseContactInfo(country) as ContactDetail[]).filter(
        (detail) => detail.isVisibleForChina,
      ),
    [country],
  );

  const handleClickEvent = (buttonName: string) => {
    gtagEventEmitter({
      action: "bottom_button_clicked",
      category: "Footer Section",
      button_name: buttonName,
    });
  };

  const renderLinkColumn = (headingKey: string, links: FooterLink[], testId: string) => (
    <div className={classes.linkColumn}>
      <div data-testid={testId} className={classes.columnHeading}>
        {t(headingKey)}
      </div>
      <div className={classes.linkList}>
        {links.map((item) => (
          <div className={classes.linkRow} key={item.gaKey}>
            <HelmetServer>
              <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
                {getSiteNavSchema(item.name, item.link)}
              </script>
            </HelmetServer>
            <CustomLink
              href={item.link}
              target="_blank"
              rel="noreferrer"
              isExternal
              dataTestId={item.name}
              onClick={() => handleClickEvent(item.gaKey)}
            >
              {t(item.name)}
            </CustomLink>
            {item.extraText && (
              <span data-testid="footer-option-tag" className={classes.badge}>
                {t(item.extraText)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className={classes.footerContainer}>
      <div className={classes.container} data-testid="footer-section">
        <div className={classes.grid}>
          {/* ── Left column: brand, QR tiles, Trustpilot, app + payment ── */}
          <div className={classes.leftColumn}>
            <div className={classes.brandBlock}>
              <LazyImage
                dataTestId="footer-Amber-logo"
                src={`${config.IMAGE_STATIC_URL}/images/logo/amber-china-logo-dark.svg?auto=format&q=40`}
                className={classes.logo}
                alt="amber"
                width="174"
                height="28"
              />
              <div data-testid="footer-amber-rights-reserved" className={classes.copyright}>
                {`amber © ${year}. ${t("home.footer.allrightsreserved")}`}
              </div>
            </div>

            <div className={classes.qrRow}>
              {CHINA_FOOTER_QR_TILES.map((tile) => (
                <div className={classes.qrTile} key={tile.id}>
                  <LazyImage
                    dataTestId={`footer-qr-${tile.id}`}
                    src={tile.src}
                    className={classes.qrImage}
                    alt={t(tile.labelKey)}
                    width={QR_SIZE}
                    height={QR_SIZE}
                  />
                  <div className={classes.qrLabel}>{t(tile.labelKey)}</div>
                </div>
              ))}
            </div>

            <TrustPilotCustomWidget variant="footerChina" />

            <div className={classes.appPaymentCard}>
              <div className={classes.appPaymentBlock}>
                <div data-testid="footer-get-amber-app" className={classes.appPaymentHeading}>
                  {t("home.footer.gettheapp")}
                </div>
                {/* Design order: Android mark at x=0, App Store at x=44. */}
                <div className={classes.storeIcons}>
                  <CustomLink
                    href={CHINA_ANDROID_DOWNLOAD_LINK}
                    target="_blank"
                    rel="noreferrer"
                    isExternal
                    dataTestId="footer-get-app-link-Android"
                    onClick={() => handleClickEvent("Android Store")}
                  >
                    <span
                      className={classes.storeIcon}
                      data-testid="Android-store-Image"
                      aria-label="android store"
                      role="img"
                    >
                      <AndroidStoreIcon height="22" width="22" />
                    </span>
                  </CustomLink>
                  <CustomLink
                    href={APP_DOWNLOAD_APP_STORE_LINK}
                    target="_blank"
                    rel="noreferrer"
                    isExternal
                    dataTestId="footer-get-app-link-Appstore"
                    onClick={() => handleClickEvent("App Store")}
                  >
                    <span className={classes.storeIcon}>
                      <LazyImage
                        dataTestId="App-store-Image"
                        src={getImagePath(`${config.IMAGE_STATIC_URL}/images/app-store.svg`)}
                        alt="app store"
                        className={classes.appStoreImg}
                        width="23"
                        height="23"
                      />
                    </span>
                  </CustomLink>
                </div>
              </div>

              <span className={classes.verticalRule} />

              <div className={classes.appPaymentBlock}>
                <div data-testid="footer-payment-options" className={classes.appPaymentHeading}>
                  {t("home.footer.paymentoptions")}
                </div>
                <div className={classes.paymentLogosRow}>
                  <ChinaPaymentLogos />
                </div>
              </div>
            </div>

            {/* Mainland licensing. Not drawn in the Figma frame but legally required on .cn. */}
            <div className={classes.icpBlock}>
              <a
                className={classes.icpLink}
                href={ICP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {ICP_LICENCE_NUMBER}
              </a>
            </div>
          </div>

          {/* ── Link columns ── */}
          <div className={classes.columnsGroup}>
            <div className={classes.columnsInner}>
              {renderLinkColumn(
                "home.footer.company",
                footerListLeftChina,
                "footer-company-section",
              )}
              {renderLinkColumn(
                "home.footer.discover",
                footerListRightChina,
                "footer-discover-section",
              )}
              {renderLinkColumn(
                "home.footer.support",
                footerSupportListChinaDesktop,
                "footer-support-section",
              )}

              {/* ── Contact us + follow us ── */}
              <div className={classes.contactColumn}>
                <div className={classes.contactBlock}>
                  <div data-testid="footer-contactUs-section" className={classes.columnHeading}>
                    {t("home.footer.contactus")}
                  </div>
                  <div className={classes.contactStack}>
                    {contactDetails.map((detail) => (
                      <CustomLink
                        key={detail.label}
                        href={detail.link}
                        target="_blank"
                        rel="noreferrer"
                        isExternal
                        className={classes.contactRow}
                        dataTestId={detail.label}
                        onClick={() => handleClickEvent(detail.label)}
                      >
                        <span className={classes.contactIcon}>{detail.icon}</span>
                        <span className={classes.contactLabel}>{t(detail.label)}</span>
                      </CustomLink>
                    ))}
                  </div>
                </div>

                <div className={classes.followBlock}>
                  <div className={classes.followLabel}>{`${t("home.footer.followus")}:`}</div>
                  <div className={classes.followIcons}>
                    {chinaSocialMediaData.map((media) => (
                      <div key={media.id}>
                        <HelmetServer>
                          <script type="application/ld+json" suppressHydrationWarning nonce={nonce}>
                            {getSiteNavSchema(media.name, media.link)}
                          </script>
                        </HelmetServer>
                        <LazyImage
                          dataTestId={`footer-social-${media.id}`}
                          src={media.icon}
                          alt={media.name}
                          className={classes.followIcon}
                          width="20"
                          height="20"
                          onClick={() =>
                            customWindowOpen({
                              url: media.link,
                              target: "_blank",
                              isExternal: true,
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default wrapperHOC(FooterDesktopChina, {
  componentName: "FooterDesktopChina-FooterV2",
  showForChina: true,
  showForWhiteLable: false,
});
